import React, { useState, useEffect, useRef } from "react";
import {
  formatCurrency,
  getDataWithComp,
  getDataWithJoin,
  parseCurrency,
} from "../../../utils/SamuiUtils";
import { ReloadOutlined, LoadingOutlined } from "@ant-design/icons";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const ItemModal = ({
  formDetailList,
  showItemModal,
  handleItemClose,
  itemDataList,
  onRowSelectItem,
  onRowSelectItemEnter,
  selectionMode,
  setSelectionMode,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItemDataList, setFilteredItemDataList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [masterItemDataList, setMasterItemDataList] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [showInactiveItems, setShowInactiveItems] = useState(false);

  const [colourMaster, setColourMaster] = useState([]);
  const [sizeMaster, setSizeMaster] = useState([]);

  const searchInputRef = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const buildBaseQuery = ({ companyId, whId, statusFilter }) => {
    return {
      select: `
        v.Item_Id, v.Item_Barcode, v.Item_Code, 
        v.Item_Name, v.Item_Name_EN, v.Item_Short_Name, 
        v.Item_Unit_IN, v.Item_Unit_ST, v.Item_Unit_OUT, 
        v.Item_Cost, v.Item_Price, v.Item_PriceA, v.Item_PriceB, 
        v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line,
        ISNULL(wh.Item_Onhand, 0) AS Item_Onhand
      `,
      from: `View_Set_Item v`,
      join: `
        LEFT JOIN Tb_Set_ItmGroup grp ON (grp.Group_Id = v.Item_Group)
        ${
          whId
            ? `LEFT JOIN (
                SELECT wh.*, ROW_NUMBER() OVER (PARTITION BY wh.Item_Id ORDER BY wh.Item_Onhand DESC) AS rn
                FROM API_1101_WH_ITEM_ONHAND wh
                WHERE wh.WH_Id = ${whId}
              ) wh ON (wh.Item_Id = v.Item_Id AND wh.rn = 1)`
            : ""
        }
      `,
      baseWhere: `
        WHERE v.Comp_Id = ${companyId} 
          AND grp.Comp_Id = ${companyId}
          AND grp.Group_Code = 'G'
          ${statusFilter}
          ${whId ? "AND wh.Item_Onhand > 0" : ""}
      `,
      orderBy: `
        ORDER BY v.Item_Short_Name, v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line
      `,
    };
  };

  // โหลดข้อมูลสินค้าจาก Database (โหลดทั้งหมด)
  const loadItemsFromDB = async (searchAfterLoad = null) => {
    setIsLoadingItems(true);
    try {
      const companyId = localStorage.getItem("company");
      const whId = localStorage.getItem("WHID")
        ? Number(localStorage.getItem("WHID"))
        : null;

      const statusFilter = showInactiveItems ? "" : "AND ISNULL(v.is_Active, v.Item_Status) = 'Y'";
      const q = buildBaseQuery({ companyId, whId, statusFilter });

      const data = await getDataWithJoin(
        q.select,
        q.from,
        q.join,
        `
          ${q.baseWhere}
          ${q.orderBy}
        `
      );

      if (data && data.length > 0) {
        setMasterItemDataList(data);

        const s = normalize(searchAfterLoad);
        if (s) {
          const filtered = data.filter((item) => {
            const code = normalize(item.Item_Code);
            const name = normalize(item.Item_Name);
            const barcode = normalize(item.Item_Barcode);
            return code.includes(s) || name.includes(s) || barcode.includes(s);
          });
          setFilteredItemDataList(filtered);
        } else {
          setFilteredItemDataList(data);
        }
      } else {
        setMasterItemDataList([]);
        setFilteredItemDataList([]);
      }
    } catch (error) {
      console.error("❌ Error loading items:", error);
      alert(`เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message || error}`);
      setMasterItemDataList([]);
      setFilteredItemDataList([]);
    } finally {
      setIsLoadingItems(false);
    }
  };

  // ✅ ค้นหาเฉพาะคำค้นจาก DB (ใช้ตอนสแกน/กด Enter) — เร็วกว่าโหลดทั้งหมดมาก
  const searchItemsFromDB = async (rawSearch) => {
    const searchValue = normalize(rawSearch);
    if (!searchValue) return { data: [], searchValue };

    setIsLoadingItems(true);
    try {
      const companyId = localStorage.getItem("company");
      const whId = localStorage.getItem("WHID")
        ? Number(localStorage.getItem("WHID"))
        : null;

      const statusFilter = showInactiveItems ? "" : "AND ISNULL(v.is_Active, v.Item_Status) = 'Y'";
      const q = buildBaseQuery({ companyId, whId, statusFilter });

      // ป้องกัน quote พัง (อย่างน้อยแทน ' -> '')
      const safe = searchValue.replace(/'/g, "''");

      // หมายเหตุ: ใช้ exact match กับ Code/Barcode และ LIKE กับ Name (ยืดหยุ่น)
      const searchWhere = `
        AND (
          LOWER(LTRIM(RTRIM(ISNULL(v.Item_Code,'')))) = '${safe}'
          OR LOWER(LTRIM(RTRIM(ISNULL(v.Item_Barcode,'')))) = '${safe}'
          OR LOWER(LTRIM(RTRIM(ISNULL(v.Item_Name,'')))) LIKE '%${safe}%'
        )
      `;

      const data = await getDataWithJoin(
        q.select,
        q.from,
        q.join,
        `
          ${q.baseWhere}
          ${searchWhere}
          ${q.orderBy}
        `
      );

      return { data: Array.isArray(data) ? data : [], searchValue };
    } catch (error) {
      console.error("❌ Error searching items:", error);
      alert(`เกิดข้อผิดพลาดในการค้นหา: ${error.message || error}`);
      return { data: [], searchValue };
    } finally {
      setIsLoadingItems(false);
    }
  };

  // ค้นหาภายในข้อมูลที่โหลดมาแล้ว (ตอนพิมพ์)
  const handleSearchItemLocal = () => {
    const s = normalize(searchTerm);
    if (!s) {
      setFilteredItemDataList(masterItemDataList);
      return;
    }

    const filtered = masterItemDataList.filter((item) => {
      const code = normalize(item.Item_Code);
      const name = normalize(item.Item_Name);
      const barcode = normalize(item.Item_Barcode);
      return code.includes(s) || name.includes(s) || barcode.includes(s);
    });

    setFilteredItemDataList(filtered);
  };

  const handleInputQtyChange = (itemId, value) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.Item_Id === itemId ? { ...item, inputQty: parseFloat(value) || 0 } : item
      )
    );
  };

  // Auto search เมื่อพิมพ์ (local)
  useEffect(() => {
    if (showItemModal) {
      handleSearchItemLocal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, showItemModal, masterItemDataList]);

  // โหลดข้อมูลครั้งแรกเมื่อเปิด Modal
  useEffect(() => {
    if (showItemModal) {
      setSearchTerm("");
      setSelectedItems([]);

      // ถ้ามี itemDataList ส่งมาให้ใช้เลย
      if (Array.isArray(itemDataList)) {
        setMasterItemDataList(itemDataList);
        setFilteredItemDataList(itemDataList);
      } else if (masterItemDataList.length === 0) {
        loadItemsFromDB();
      } else {
        setFilteredItemDataList(masterItemDataList);
      }

      if (window.location.pathname === "/payment-voucher") {
        setSelectedItems([]);
      }

      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showItemModal, itemDataList]);

  // ✅ เปลี่ยน showInactiveItems แล้ว reload ใหม่ตอน modal เปิดอยู่
  useEffect(() => {
    if (showItemModal && !Array.isArray(itemDataList)) {
      loadItemsFromDB(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactiveItems]);

  useEffect(() => {
    if (window.location.pathname === "/payment-voucher") {
      if (showItemModal) {
        setSelectedItems(
          formDetailList
            .map((item) =>
              masterItemDataList.find((filtered) => filtered.Item_Id === item.itemId)
            )
            .filter(Boolean)
        );
      }
    }
  }, [showItemModal, formDetailList, masterItemDataList]);

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedItems(filteredItemDataList);
    } else {
      setSelectedItems([]);
    }
  };

  const handleCheckboxChange = (item) => {
    if (
      window.location.pathname === "/payment-voucher" ||
      window.location.pathname === "/warehouse-stock"
    ) {
      setSelectedItems((prevSelected) => {
        const isSelected = prevSelected.some(
          (selected) => selected.Item_Id === item.Item_Id
        );
        return isSelected
          ? prevSelected.filter((selected) => selected.Item_Id !== item.Item_Id)
          : [...prevSelected, item];
      });
    } else {
      setSelectedItems((prevSelected) => {
        const existingItemIndex = prevSelected.findIndex(
          (selected) => selected.Item_Id === item.Item_Id
        );

        if (existingItemIndex !== -1) {
          return prevSelected.filter((_, index) => index !== existingItemIndex);
        }
        if (selectionMode === "addQuantity") {
          return [...prevSelected, { ...item, itemQty: 1 }];
        } else if (selectionMode === "addNewRow") {
          return [...prevSelected, { ...item, itemQty: 1 }];
        }

        return prevSelected;
      });
    }
  };

  // ✅ รองรับการสแกน: Enter / NumpadEnter → ค้น DB แบบเฉพาะคำค้น แล้ว auto select ถ้าเจอ 1 รายการ
  const handleKeyDown = async (e) => {
    const isEnter = e.key === "Enter" || e.key === "NumpadEnter";
    if (!isEnter) return;

    e.preventDefault();

    const raw = searchTerm;
    const s = normalize(raw);
    if (!s) return;

    // 1) ค้นจาก DB เฉพาะคำค้น (ไวและแม่น)
    const { data } = await searchItemsFromDB(s);

    if (data.length > 1) {
      // เจอหลายรายการ แสดงในตารางให้เลือก
      setFilteredItemDataList(data);
      return;
    }

    if (data.length === 1) {
      // เจอ 1 รายการ ส่งไปเลย
      const foundItem = data[0];
      onRowSelectItemEnter([{ ...foundItem, itemQty: 1 }]);

      // เคลียร์ช่อง แล้วให้แสดงข้อมูลทั้งหมดที่มีใน master (หรือโหลดใหม่ถ้าไม่มี)
      setSearchTerm("");

      if (masterItemDataList.length > 0) {
        setFilteredItemDataList(masterItemDataList);
      } else {
        // ถ้ายังไม่เคยโหลด master ให้โหลด
        loadItemsFromDB();
      }
      return;
    }

    // 2) ถ้าไม่เจอจาก DB → fallback แสดง master ที่มี หรือโหลดทั้งหมด
    if (masterItemDataList.length > 0) {
      setFilteredItemDataList(masterItemDataList);
    } else {
      loadItemsFromDB();
    }
    console.warn("⚠️ ไม่พบสินค้าที่ค้นหา:", raw);
  };

  const WH_Name = localStorage?.getItem("WHName") || "";

  return (
    <>
      <div
        className={`modal ${showItemModal ? "show" : ""}`}
        style={{ display: showItemModal ? "block" : "none", height: "475px" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content" style={{ width: "1090px" }}>
            <div className="modal-header">
              <h5 className="modal-title">รายชื่อสินค้า</h5>
              <button type="button" className="close" onClick={handleItemClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">
              {window.location.pathname === "/payment-voucher" ||
              window.location.pathname === "/warehouse-stock" ? null : (
                <div className="d-flex align-items-center ">
                  <label className="me-3" style={{ display: "flex", width: "30%" }}>
                    <input
                      type="radio"
                      value="addQuantity"
                      checked={selectionMode === "addQuantity"}
                      onChange={() => setSelectionMode("addQuantity")}
                      style={{ width: "25px" }}
                    />
                    <span className="ms-2">บวกรายการสินค้าเดิม</span>
                  </label>
                  <label className="ms-4" style={{ display: "flex", width: "100%" }}>
                    <input
                      type="radio"
                      value="addNewRow"
                      checked={selectionMode === "addNewRow"}
                      onChange={() => setSelectionMode("addNewRow")}
                      style={{ width: "25px" }}
                    />
                    <span className="ms-2">เพิ่มรายการสินค้าใหม่</span>
                  </label>
                </div>
              )}

              <div
                className="form-group"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div style={{ position: "relative", flex: 1 }}>
                  <input
                    type="text"
                    ref={searchInputRef}
                    className="form-control"
                    placeholder="ค้นหา รหัสสินค้า หรือ ชื่อสินค้า / สแกนบาร์โค้ดแล้วกด Enter"
                    value={searchTerm || ""}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ paddingRight: "40px" }}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2"
                      style={{
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => {
                        setSearchTerm("");
                        setFilteredItemDataList(masterItemDataList);
                      }}
                      aria-label="clear search"
                      title="ล้างคำค้นหา"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => loadItemsFromDB(searchTerm)}
                  disabled={isLoadingItems}
                  title="โหลดข้อมูลใหม่"
                  className="btn btn-primary d-flex align-items-center justify-content-center"
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "6px",
                    padding: 0,
                  }}
                >
                  {isLoadingItems ? (
                    <LoadingOutlined style={{ fontSize: 22, color: "#fff" }} />
                  ) : (
                    <ReloadOutlined style={{ fontSize: 22, color: "#fff" }} />
                  )}
                </button>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={showInactiveItems}
                    onChange={(e) => setShowInactiveItems(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ width: "560px" }}>แสดงสินค้ารายการเก่า</span>
                </label>
              </div>

              <div className="table-responsive" style={{ overflow: "hidden" }}>
                {/* TABLE HEADER */}
                <table
                  className="table table-striped"
                  style={{
                    tableLayout: "fixed",
                    width: "1045px",
                    marginBottom: 0,
                  }}
                >
                  <colgroup>
                    <col style={{ width: "30px" }} />
                    <col style={{ width: "50px" }} />
                    <col style={{ width: "150px" }} />
                    <col style={{ width: "33%" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                  </colgroup>
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-center">#</th>
                      <th className="text-center">
                        <input
                          type="checkbox"
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          checked={
                            filteredItemDataList.length > 0 &&
                            selectedItems.length === filteredItemDataList.length
                          }
                          style={{ width: "40px" }}
                        />
                      </th>
                      <th className="text-center">รหัสสินค้า</th>
                      <th className="text-center">ชื่อสินค้า</th>
                      <th className="text-center">
                        คงเหลือ{" "}
                        {window.location.pathname === "/billofsale" ? WH_Name : ""}
                      </th>
                      <th className="text-center">ราคา1</th>
                      <th className="text-center">ราคา2</th>
                      <th className="text-center">ราคา3</th>
                    </tr>
                  </thead>
                </table>

                {/* SCROLLABLE BODY */}
                <div style={{ maxHeight: 150, overflowY: "auto" }}>
                  <table
                    className="table table-striped"
                    style={{
                      tableLayout: "fixed",
                      width: "1045px",
                      marginBottom: 0,
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "30px" }} />
                      <col style={{ width: "50px" }} />
                      <col style={{ width: "150px" }} />
                      <col style={{ width: "33%" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "10%" }} />
                    </colgroup>
                    <tbody>
                      {isLoadingItems ? (
                        <tr>
                          <td colSpan={8} className="text-center">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <div>กำลังโหลดข้อมูล...</div>
                          </td>
                        </tr>
                      ) : filteredItemDataList.length > 0 ? (
                        filteredItemDataList.map((item, index) => (
                          <tr
                            key={item.Item_Id || index}
                            style={{
                              cursor: "pointer",
                              backgroundColor: selectedItems.some(
                                (selectedItem) => selectedItem.Item_Id === item.Item_Id
                              )
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                              transition: "background-color 0.3s ease",
                            }}
                          >
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.some(
                                  (selected) => selected.Item_Id === item.Item_Id
                                )}
                                onChange={() => handleCheckboxChange(item)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: "pointer", width: "40px" }}
                              />
                            </td>
                            <td className="text-center">{item.Item_Code}</td>
                            <td className="text-left">{item.Item_Name}</td>
                            <td className="text-center">
                              {window.location.pathname === "/warehouse-stock" ? (
                                selectedItems.some(
                                  (selected) => selected.Item_Id === item.Item_Id
                                ) ? (
                                  <input
                                    type="number"
                                    value={
                                      selectedItems.find(
                                        (selected) => selected.Item_Id === item.Item_Id
                                      )?.inputQty ?? 0
                                    }
                                    onChange={(e) =>
                                      handleInputQtyChange(item.Item_Id, e.target.value)
                                    }
                                    onFocus={(e) => e.target.select()}
                                    className="form-control text-end"
                                  />
                                ) : (
                                  0
                                )
                              ) : (
                                formatCurrency(item.Item_Onhand)
                              )}
                            </td>
                            <td className="text-center">
                              {formatCurrency(item.Item_Price)}
                            </td>
                            <td className="text-center">
                              {formatCurrency(item.Item_PriceA)}
                            </td>
                            <td className="text-center">
                              {formatCurrency(item.Item_PriceB)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center">
                            ไม่พบข้อมูล
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ justifyContent: "space-between", paddingTop: 0 }}>
              <div>
                <p>รายการสินค้าทั้งหมด : {filteredItemDataList.length}</p>
              </div>
              <div style={{ display: "flex" }}>
                <button
                  className="btn btn-primary"
                  onClick={() => onRowSelectItem(selectedItems)}
                  style={{ marginRight: "10px" }}
                >
                  ยืนยัน
                </button>
                <button className="btn btn-danger" onClick={handleItemClose}>
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showItemModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ItemModal;